import React, { useId, useState } from "react";
import { Link } from "react-router-dom";
import { startCase } from "lodash";
import { Dialog } from "@headlessui/react";
import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import MapView from "@components/shared/MapView";
import DeclarationTag from "@components/shared/DeclarationTag";
import ExtractionPointStatusTags from "@components/shared/ExtractionPointStatusTags";
import Tag from "@components/shared/Tag";
import Loading from "@components/shared/Loading";
import LoadFailed, { showsOwnFailure } from "@components/zenith/ui/LoadFailed";
import Table from "@components/zenith/ui/Table";
import { useAppContext } from "@context/AppContext";
import { useAllDeclarations } from "@hooks/query/zenith/useAllDeclarations";
import { extractionRightTypes } from "@services/extractionRight";
import { READING_CALCULATION } from "@services/declarations";
import { formatVolume } from "@utils/formatVolume";
import { parseLatLng } from "@utils/parseLatLng";
import { formatSchemeDay } from "@utils/schemeDay";
import ENV from "@config/env";
import { CreatedBy } from "./TransactionHistory";

/** Reads shown before "See more". */
const LATEST_READS = 3;

/**
 * One offtake's details, meter reads (newest first) and map. Always open;
 * mount it only while an offtake is selected.
 */
const OfftakeDetailModal: React.FunctionComponent<{
  point: any;
  onClose: () => void;
}> = ({ point, onClose }) => {
  const { t } = useTranslation();
  const { isBetweenPeriods } = useAppContext();
  const readsTitleId = useId();
  const [showAllReads, setShowAllReads] = useState(false);
  const latLng = parseLatLng(point.address?.location);

  const {
    data: declarations = [],
    isLoading,
    isError,
  } = useAllDeclarations({
    params: { extractionPointIds: [point.id] },
    select: (data: any) => data.declarations.filter((d: any) => !d.deletedAt),
    ...showsOwnFailure,
  });

  const details: [string, React.ReactNode][] = [
    [
      t("extraction_point.is_active"),
      <ExtractionPointStatusTags point={point} />,
    ],
    [t("extraction_point.source"), startCase(point.source)],
    [t("extraction_point.lat"), latLng.lat?.toFixed(5) ?? "-"],
    [t("extraction_point.long"), latLng.lng?.toFixed(5) ?? "-"],
    ...(ENV.CLIENT_ID === "seqwater"
      ? ([
          [t("extraction_point.group"), point.group ?? "-"],
          [t("extraction_point.sequence"), point.sequence ?? "-"],
          ...(point.description
            ? [[t("common.description"), point.description]]
            : []),
        ] as [string, React.ReactNode][])
      : []),
  ];
  const meterDetails: [string, React.ReactNode][] = [
    [t("meter.serial_no"), point.meter?.serialNo ?? t("meter.unmetered")],
    [
      t("zenith.zone_accounts.last_read"),
      point.lastDeclaration
        ? formatSchemeDay(point.lastDeclaration.readAt)
        : "-",
    ],
  ];

  const reads = declarations.map(
    (declaration: any, index: number, all: any[]) =>
      readRow(declaration, index, all, point, t),
  );

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-gray-500/75" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-6xl overflow-hidden rounded-lg bg-white shadow-xl">
            <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-3.5">
              <div>
                <Dialog.Title className="text-base font-bold text-primary-3">
                  {t("zenith.zone_accounts.offtake_title", {
                    name: point.name,
                  })}
                </Dialog.Title>
                <p className="text-sm text-gray-500">
                  {point.level0WRS?.identifier} · {point.level0WRS?.name}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("common.close") as string}
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </header>

            <div className="space-y-5 p-5">
              <div className="grid gap-5 lg:grid-cols-5">
                <div className="min-w-0 space-y-4 lg:col-span-3">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <dl className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                      {details.map(([label, value]) => (
                        <div key={label} className="flex items-center gap-1.5">
                          <dt className="text-gray-500">{label}</dt>
                          <dd className="text-gray-900">{value}</dd>
                        </div>
                      ))}
                    </dl>
                    <Link
                      to="/zenith/workflows/water_declaration"
                      className="btn-secondary shrink-0 whitespace-nowrap text-xs"
                    >
                      {t("zenith.workflows.water_declaration")}
                    </Link>
                  </div>

                  <dl className="grid grid-cols-2 gap-2.5">
                    {meterDetails.map(([label, value]) => (
                      <div
                        key={label}
                        className="rounded-lg bg-gray-50 px-3 py-2.5"
                      >
                        <dt className="text-xxs font-bold uppercase tracking-widest text-gray-500">
                          {label}
                        </dt>
                        <dd className="mt-0.5 text-sm text-gray-900">
                          {value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>

                <section className="space-y-2 lg:col-span-2">
                  <h3 className="text-sm font-bold text-primary-3">
                    {t("zenith.zone_accounts.location")}
                  </h3>
                  <div
                    data-testid="offtake-map"
                    className="h-56 overflow-hidden rounded-lg border border-gray-200"
                  >
                    {latLng.isValid ? (
                      <MapView
                        fitContainer
                        zoomLevel={14}
                        markers={[
                          { lat: latLng.lat ?? 0, lng: latLng.lng ?? 0 },
                        ]}
                      />
                    ) : (
                      <div className="pt-10 text-center text-sm font-semibold text-primary-3">
                        {t("shared.map.invalid_data")}
                      </div>
                    )}
                  </div>
                </section>
              </div>

              <section className="space-y-2">
                <h3
                  id={readsTitleId}
                  className="text-sm font-bold text-primary-3"
                >
                  {t("zenith.zone_accounts.meter_read_history")}
                </h3>
                {isLoading ? (
                  <Loading />
                ) : isError ? (
                  <LoadFailed />
                ) : (
                  <Table
                    aria-labelledby={readsTitleId}
                    rowKey={(read: any) => read.id}
                    rows={showAllReads ? reads : reads.slice(0, LATEST_READS)}
                    // Reads list only for the active period, so between periods there are none to show.
                    empty={
                      isBetweenPeriods
                        ? t("zenith.dashboard.no_active_period")
                        : t("declaration.no_declaration")
                    }
                    // Each read is already its cells, keyed by column.
                    columns={(
                      [
                        ["meterId", t("meter.serial_no")],
                        ...(ENV.CLIENT_ID === "seqwater"
                          ? [["meterStatus", t("meter.meter_status")]]
                          : []),
                        ["readAt", t("declaration.read_at")],
                        ["reading", t("declaration.reading")],
                        ["usage", t("declaration.usage")],
                        ["allocationUsage", t("declaration.allocation_usage")],
                        ["tag", t("common.tag")],
                        ["createdBy", t("common.created_by")],
                      ] as [string, string][]
                    ).map(([key, label]) => ({
                      key,
                      label,
                      render: (read: any) => read[key],
                    }))}
                  />
                )}
                {reads.length > LATEST_READS ? (
                  <button
                    type="button"
                    onClick={() => setShowAllReads(shown => !shown)}
                    className="text-sm font-semibold text-primary-1 hover:underline"
                  >
                    {showAllReads ? t("common.see_less") : t("common.see_more")}
                  </button>
                ) : null}
              </section>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

/** One read as table cells. */
const readRow = (
  declaration: any,
  index: number,
  declarations: any[],
  point: any,
  t: (key: string) => string,
) => {
  const isDecommissioned =
    declaration.meter?.deletedAt && !declaration.meter.isActive;
  const isActive =
    declaration.meter?.isActive && declaration.meter.id === point.meter?.id;
  const usage = declaration.requireManualIntervention ? 0 : declaration.volume;

  const {
    tag,
    allocationUsage: partAllocationUsage,
    usage: unAllocatedUsage,
    saUsage,
    whUsage,
  } = determineTagValue(declaration, index, declarations);

  const allocationUsage = [
    READING_CALCULATION.STOCK_AND_DOMESTIC.toString(),
    READING_CALCULATION.METER_USAGE_ADJUSTMENT.toString(),
  ].includes(tag)
    ? partAllocationUsage
    : declaration.isUnallocated
      ? 0
      : usage;

  return {
    id: declaration.id,
    meterId: declaration.meter?.serialNo ?? t("meter.unmetered"),
    meterStatus: (
      <Tag status={isActive ? "success" : "error"}>
        {isDecommissioned
          ? t("common.decommission")
          : isActive
            ? t("common.active")
            : t("common.inactive")}
      </Tag>
    ),
    readAt: formatSchemeDay(declaration.readAt),
    reading: declaration.meter?.serialNo
      ? formatVolume(declaration.reading, "")
      : "-",
    usage: formatVolume(usage),
    allocationUsage: (
      <Tag
        status={
          allocationUsage < 0
            ? "error"
            : allocationUsage > 0
              ? "success"
              : "default"
        }
        className="inline-flex gap-1 items-center pl-1 pr-2"
      >
        <span>
          {allocationUsage < 0 && <ArrowDownIcon className="w-4 h-4" />}
          {allocationUsage > 0 && <ArrowUpIcon className="w-4 h-4" />}
        </span>
        {formatVolume(allocationUsage)}
      </Tag>
    ),
    tag: tag ? (
      <DeclarationTag
        value={tag}
        volume={partAllocationUsage !== 0 ? unAllocatedUsage : 0}
        saVolume={saUsage}
        whVolume={whUsage}
      />
    ) : null,
    createdBy: <CreatedBy user={declaration.createdByUser} />,
  };
};

const proportionOf = (declaration: any, type: number) =>
  declaration.classProportions?.rightTypeProportions?.find((proportion: any) =>
    proportion.hasOwnProperty(type),
  )?.[type] ?? 0;

/** A read's tag and how its usage splits across right types; `declarations` are newest first. */
const determineTagValue = (
  declaration: any,
  index: number,
  declarations: any[],
): {
  tag: string;
  usage: number;
  allocationUsage: number;
  saUsage: number;
  whUsage: number;
} => {
  let tagValue = declaration.tag;

  if (tagValue === READING_CALCULATION.METER_USAGE_ADJUSTMENT) {
    return {
      tag: READING_CALCULATION.METER_USAGE_ADJUSTMENT,
      usage: proportionOf(declaration, extractionRightTypes.SD),
      allocationUsage:
        proportionOf(declaration, extractionRightTypes.WA) +
        proportionOf(declaration, -1),
      saUsage: proportionOf(declaration, extractionRightTypes.SA),
      whUsage: proportionOf(declaration, extractionRightTypes.WH),
    };
  }
  const hasStockAndDomestic =
    declaration.classProportions?.rightTypeProportions?.some(
      (proportion: any) => proportion.hasOwnProperty(extractionRightTypes.SD),
    );

  if (hasStockAndDomestic) {
    return {
      tag: READING_CALCULATION.STOCK_AND_DOMESTIC,
      usage: proportionOf(declaration, extractionRightTypes.SD),
      allocationUsage:
        proportionOf(declaration, extractionRightTypes.WA) +
        proportionOf(declaration, -1),
      saUsage: 0,
      whUsage: 0,
    };
  }

  // Water harvesting logic
  const nextDeclaration = declarations[index - 1];
  const waterHarvestingStarted =
    tagValue === READING_CALCULATION.WATER_HARVESTING &&
    !declaration.isUnallocated;
  const waterHarvestingStartedWithSpecialAnnouncement =
    tagValue === READING_CALCULATION.SPECIAL_ANNOUNCEMENT &&
    declaration.isUnallocated &&
    nextDeclaration?.tag === READING_CALCULATION.WATER_HARVESTING;

  const waterHarvestingEnded =
    tagValue === READING_CALCULATION.WATER_HARVESTING &&
    (!nextDeclaration ||
      nextDeclaration.tag !== READING_CALCULATION.WATER_HARVESTING);

  if (waterHarvestingStarted) {
    tagValue = READING_CALCULATION.WATER_HARVESTING_START;
  } else if (waterHarvestingEnded) {
    tagValue = READING_CALCULATION.WATER_HARVESTING_END;
  } else if (waterHarvestingStartedWithSpecialAnnouncement) {
    return {
      tag: READING_CALCULATION.WATER_HARVESTING_START,
      usage: 0,
      allocationUsage: 0,
      saUsage: proportionOf(declaration, extractionRightTypes.SA),
      whUsage: 0,
    };
  }

  return {
    tag: tagValue,
    usage: 0,
    allocationUsage: 0,
    saUsage: 0,
    whUsage: 0,
  };
};

export default OfftakeDetailModal;
